/**
 * Ably Real-Time Provider
 *
 * Provides Ably client context with token-based authentication.
 * Handles connection lifecycle including mobile background/foreground transitions.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import * as Ably from 'ably';
import { useAppSelector } from '@/store/store';
import { selectCurrentUserId, selectIsLoggedIn } from '@/store/slices/authSlice';
import { apiClient } from '@/lib/apiClient';
import { useRealtimeNotifications } from './useRealtimeNotifications';

// Connection states
type ConnectionState = 'initialized' | 'connecting' | 'connected' | 'disconnected' | 'suspended' | 'failed';

interface AblyContextValue {
  client: Ably.Realtime | null;
  connectionState: ConnectionState;
  isConnected: boolean;
  isEnabled: boolean;
  error: Error | null;
}

const AblyContext = createContext<AblyContextValue>({
  client: null,
  connectionState: 'initialized',
  isConnected: false,
  isEnabled: false,
  error: null,
});

// Check if Ably is enabled via environment variable
const ABLY_ENABLED = import.meta.env.VITE_ABLY_ENABLED === 'true';

// Background disconnect delay (30 seconds)
const BACKGROUND_DISCONNECT_DELAY = 30000;

interface AblyProviderProps {
  children: React.ReactNode;
}

/**
 * Internal component that subscribes to global real-time channels.
 * Placed inside the provider so it persists across page transitions.
 */
function GlobalRealtimeSubscriptions() {
  useRealtimeNotifications();
  return null;
}

/**
 * Token request function that fetches auth token from backend
 */
async function fetchAblyToken(collabLists?: string[]): Promise<Ably.TokenRequest> {
  const response = await apiClient.post('/realtime/token/', {
    collabLists: collabLists || [],
  });
  return response.data;
}

export function AblyProvider({ children }: AblyProviderProps) {
  const isAuthenticated = useAppSelector(selectIsLoggedIn);
  const userId = useAppSelector(selectCurrentUserId);

  // Early bailout for unauthenticated users - no Ably code runs at all
  // This prevents GlobalRealtimeSubscriptions from mounting and avoids race conditions
  if (!ABLY_ENABLED || !isAuthenticated || !userId) {
    return (
      <AblyContext.Provider
        value={{
          client: null,
          connectionState: 'initialized',
          isConnected: false,
          isEnabled: ABLY_ENABLED,
          error: null,
        }}
      >
        {children}
      </AblyContext.Provider>
    );
  }

  // Authenticated user - render the full provider with Ably client
  return <AuthenticatedAblyProvider>{children}</AuthenticatedAblyProvider>;
}

/**
 * Internal provider component that only renders for authenticated users.
 * This ensures all Ably hooks and subscriptions are gated behind authentication.
 */
function AuthenticatedAblyProvider({ children }: { children: React.ReactNode }) {
  const [client, setClient] = useState<Ably.Realtime | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('initialized');
  const [error, setError] = useState<Error | null>(null);

  const backgroundTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clientRef = useRef<Ably.Realtime | null>(null);

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;

      // Check for Ably "Channel detached" error by code (90001) or message
      // These are expected during cleanup when navigating away or app backgrounding
      const isChannelDetachedError =
        reason &&
        typeof reason === 'object' &&
        (
          ('code' in reason && reason.code === 90001) ||
          ('message' in reason && typeof reason.message === 'string' && reason.message.includes('Channel detached'))
        );

      // Also check if the reason is an Error with matching message
      const isChannelDetachedMessage =
        reason instanceof Error && reason.message.includes('Channel detached');

      if (isChannelDetachedError || isChannelDetachedMessage) {
        console.debug('[Ably] Suppressed expected channel detached error during cleanup');
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Initialize Ably client (we're guaranteed to be authenticated at this point)
  useEffect(() => {

    // Create Ably client with token auth
    const ablyClient = new Ably.Realtime({
      authCallback: async (tokenParams, callback) => {
        try {
          const tokenRequest = await fetchAblyToken();
          callback(null, tokenRequest);
        } catch (err) {
          console.error('[Ably] Token fetch failed:', err);
          callback(err instanceof Error ? err.message : String(err), null);
        }
      },
      autoConnect: true,
      echoMessages: false, // Don't echo messages back to sender
      disconnectedRetryTimeout: 15000, // Retry after 15s when disconnected
      suspendedRetryTimeout: 30000, // Retry after 30s when suspended
    });

    // Track connection state changes
    ablyClient.connection.on('connected', () => {
      console.debug('[Ably] Connected');
      setConnectionState('connected');
      setError(null);
    });

    ablyClient.connection.on('connecting', () => {
      console.debug('[Ably] Connecting...');
      setConnectionState('connecting');
    });

    ablyClient.connection.on('disconnected', () => {
      console.debug('[Ably] Disconnected');
      setConnectionState('disconnected');
    });

    ablyClient.connection.on('suspended', () => {
      console.debug('[Ably] Suspended');
      setConnectionState('suspended');
    });

    ablyClient.connection.on('failed', (stateChange) => {
      console.error('[Ably] Connection failed:', stateChange.reason);
      setConnectionState('failed');
      setError(stateChange.reason || new Error('Connection failed'));
    });

    clientRef.current = ablyClient;
    setClient(ablyClient);

    // Cleanup on unmount
    return () => {
      console.debug('[Ably] Closing connection');
      ablyClient.close();
      clientRef.current = null;
    };
  }, []); // No dependencies - only runs on mount/unmount since we're guaranteed authenticated

  // Handle app background/foreground transitions (mobile)
  useEffect(() => {
    if (!ABLY_ENABLED || !client) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App went to background - schedule disconnect
        console.debug('[Ably] App backgrounded, scheduling disconnect...');
        backgroundTimeoutRef.current = setTimeout(() => {
          if (clientRef.current) {
            console.debug('[Ably] Disconnecting after background timeout');
            clientRef.current.connection.close();
          }
        }, BACKGROUND_DISCONNECT_DELAY);
      } else {
        // App came to foreground - cancel disconnect and reconnect if needed
        console.debug('[Ably] App foregrounded');
        if (backgroundTimeoutRef.current) {
          clearTimeout(backgroundTimeoutRef.current);
          backgroundTimeoutRef.current = null;
        }

        if (clientRef.current && clientRef.current.connection.state !== 'connected') {
          console.debug('[Ably] Reconnecting...');
          clientRef.current.connection.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (backgroundTimeoutRef.current) {
        clearTimeout(backgroundTimeoutRef.current);
      }
    };
  }, [client]);

  const contextValue = useMemo<AblyContextValue>(
    () => ({
      client,
      connectionState,
      isConnected: connectionState === 'connected',
      isEnabled: ABLY_ENABLED,
      error,
    }),
    [client, connectionState, error]
  );

  return (
    <AblyContext.Provider value={contextValue}>
      <GlobalRealtimeSubscriptions />
      {children}
    </AblyContext.Provider>
  );
}

/**
 * Hook to access Ably context
 */
export function useAbly(): AblyContextValue {
  const context = useContext(AblyContext);
  if (!context) {
    throw new Error('useAbly must be used within AblyProvider');
  }
  return context;
}

/**
 * Hook to check if real-time is active
 */
export function useIsRealtimeActive(): boolean {
  const { isConnected, isEnabled } = useAbly();
  return isEnabled && isConnected;
}
